import React, { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { Toaster, toast } from "sonner";
import api, { formatApiErrorDetail } from "../lib/api";
import { Mail, CheckCircle2 } from "lucide-react";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [err, setErr] = useState("");

  if (user && user !== false) {
    return <Navigate to={user.role === "admin" ? "/admin" : "/scan"} replace />;
  }

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) {
      setErr("Email is required");
      return;
    }
    setErr("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email: email.trim().toLowerCase() });
      setDone(true);
      toast.success("Request received");
    } catch (e) {
      const msg = formatApiErrorDetail(e);
      setErr(msg);
      toast.error(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-stretch bg-paper" data-testid="forgot-password-page">
      <div className="flex flex-col w-full md:w-[520px] bg-paper px-6 sm:px-12 py-10 md:py-14">
        <div className="flex items-center gap-2 mb-10">
          <div className="h-10 w-10 bg-brand rounded-sm flex items-center justify-center text-white font-display font-black text-xl">
            S
          </div>
          <div className="font-display font-black text-2xl tracking-tight">
            Shelf<span className="text-brand">Life</span>
          </div>
        </div>

        {done ? (
          <div className="max-w-md" data-testid="forgot-success">
            <div className="data-sku mb-3">Request received</div>
            <h1 className="font-display font-black text-4xl leading-[0.95] tracking-tighter mb-4">
              Check with your team.
            </h1>
            <div className="flex items-start gap-2 bg-shelf-fresh-bg text-shelf-fresh px-4 py-3 rounded-sm mb-6">
              <CheckCircle2 size={18} className="mt-0.5" />
              <p className="text-sm">
                If an account exists with that email, password reset instructions
                have been generated. Email delivery is being set up; until then,
                an administrator can forward the reset link to you directly.
              </p>
            </div>
            <Link
              to="/login"
              className="btn-primary w-full inline-flex items-center justify-center"
              data-testid="forgot-back-to-login"
            >
              Back to sign in
            </Link>
          </div>
        ) : (
          <div className="max-w-md">
            <div className="data-sku mb-3">Forgot password</div>
            <h1 className="font-display font-black text-4xl leading-[0.95] tracking-tighter mb-3">
              Reset your password.
            </h1>
            <p className="text-base text-foreground/80 mb-8">
              Enter the email associated with your ShelfLife account and we'll
              send reset instructions.
            </p>

            <form onSubmit={onSubmit} className="space-y-4" data-testid="forgot-form">
              <div>
                <label className="data-sku block mb-2" htmlFor="email">Email</label>
                <input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="w-full h-14 px-4 bg-input border border-border rounded-sm font-body text-base focus:outline-none focus:border-ink"
                  placeholder="you@supermarket.co"
                  autoComplete="email"
                  data-testid="forgot-email-input"
                />
              </div>

              {err && (
                <div
                  className="bg-shelf-expired-bg text-shelf-expired px-4 py-3 rounded-sm font-mono text-xs"
                  data-testid="forgot-error"
                >
                  {err}
                </div>
              )}

              <button
                type="submit"
                disabled={loading}
                className="btn-primary w-full"
                data-testid="forgot-submit-button"
              >
                <Mail size={18} /> {loading ? "Sending…" : "Send reset link"}
              </button>

              <div className="text-center pt-3">
                <Link
                  to="/login"
                  className="font-mono text-xs uppercase tracking-wider text-ink/60 hover:text-ink"
                  data-testid="forgot-signin-link"
                >
                  Back to sign in
                </Link>
              </div>
            </form>
          </div>
        )}

        <div className="mt-auto pt-10 data-sku">© ShelfLife · Multi-department</div>
      </div>
      <Toaster position="bottom-center" richColors />
    </div>
  );
}
